/**
 * Component: Store Locator Datalayer
 * ------------------------------------------------------------------------------
 * An file to handle datalayer events for the Store Locator app/page.
 *
 */

/**
 * DOM selectors.
 */
const selectors = {
  storeLocatorContainer: '#scasl-app-container',
  storeListItem: '.scasl-location-list li',
  infoWindow: '#scasl-list-container',
  storeTitle: '#scasl-title',
}

/**
 * Create a new example object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    storeLocatorContainer: document.querySelector(selectors.storeLocatorContainer),
  }

  /**
   * Initialise component.
   */
  function init() {
    if (!nodeSelectors.storeLocatorContainer) {
      return
    }

    setEventListeners()
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    nodeSelectors.storeLocatorContainer.addEventListener('click', handleStoreLocatorClick)
  }

  /**
   * Handle clicks inside the store locator container
   * - Checks the event target and handles the click event depending on the
   *   clicked element
   * @param {MouseEvent} event - Click event
   */
  function handleStoreLocatorClick(event) {
    if (isPhoneNumberClick(event.target)) {
      handlePhoneNumberClick(event.target)
      return
    }

    if (isEmailClick(event.target)) {
      handleEmailClick(event.target)
      return
    }

    if (isListItemClick(event.target)) {
      handleListItemClick(event.target)
      return
    }
  }

  /**
   * Check if the clicked element is a phone number link
   * @param {HTMLElement} element - Clicked element
   * @returns {Boolean}
   */
  function isPhoneNumberClick(element) {
    return element.nodeName === 'A' && element.href?.includes('tel:')
  }

  /**
   * Check if the clicked element is an email link
   * @param {HTMLElement} element - Clicked element
   * @returns {Boolean}
   */
  function isEmailClick(element) {
    return element.nodeName === 'A' && element.href?.includes('mailto:')
  }

  /**
   * Check if the clicked element is a list item or its parent is a list item
   * @param {HTMLElement} element - Clicked element
   * @returns {Boolean}
   */
  function isListItemClick(element) {
    const hasListItemParent = element.closest(selectors.storeListItem)

    return Boolean(hasListItemParent)
  }

  /**
   * Get the store title for an element clicked inside the map 'infowindow' popup
   * - Finds the parent popup element.
   * - Uses querySelector to find the title element inside the popup element.
   * @param {HTMLElement} element - clicked element
   * @returns {Boolean|String} store title or false
   */
  function getSelectedStoreTitle(element) {
    const infoWindowParent = element.closest(selectors.infoWindow)
    if (!infoWindowParent) {
      return false
    }

    const storeNameElement = infoWindowParent.querySelector(selectors.storeTitle)
    if (!storeNameElement) {
      return false
    }

    return storeNameElement.innerText
  }

  /**
   * Handle phone number click.
   * - Grab the store title and the phone number value and push them to the
   *   datalayer.
   * @param {HTMLElement} element
   */
  function handlePhoneNumberClick(element) {
    const storeTitle = getSelectedStoreTitle(element)
    if (!storeTitle) {
      return
    }

    const phoneNumber = element.innerText

    const payload = {
      eventAction: phoneNumber,
      eventLabel: storeTitle.toLowerCase(),
    }

    pushStoreLocatorDataLayer(payload)
  }

  /**
   * Handle email click.
   * - Grab the store title and the email value and push them to the
   *   datalayer.
   * @param {HTMLElement} element
   */
  function handleEmailClick(element) {
    const storeTitle = getSelectedStoreTitle(element)
    if (!storeTitle) {
      return
    }

    const payload = {
      eventAction: 'email',
      eventLabel: storeTitle.toLowerCase(),
    }

    pushStoreLocatorDataLayer(payload)
  }

  /**
   * Handle list item click.
   * - Grab the store title from the list item and push to the datalayer.
   * @param {HTMLElement} element
   */
  function handleListItemClick(element) {
    const listItem = element.closest(selectors.storeListItem)
    if (!listItem) {
      return
    }

    const storeNameElement = listItem.querySelector(selectors.storeTitle)

    if (!storeNameElement || !storeNameElement.innerText) {
      return
    }

    const storeTitle = storeNameElement.innerText

    const payload = {
      eventAction: 'select store',
      eventLabel: storeTitle.toLowerCase(),
    }

    pushStoreLocatorDataLayer(payload)
  }

  /**
   * Push data to the dataLayer.
   * - Has a default object of datalayer properties shared between all datalayer
   *   events
   * - Merges the payload object with the default object to create a complete
   *   datalayer object
   * - Datalayer object is then pushed to the dataLayer.
   * @param {Object} payload - datalayer payload.
   */
  function pushStoreLocatorDataLayer(payload) {
    const defaultObject = {
      event: 'eventTracker',
      eventCategory: 'store locator',
      nonInteraction: 0,
    }

    const dataLayerData = {
      ...defaultObject,
      ...payload,
    }

    window.dataLayer = window.dataLayer || []

    window.dataLayer.push(dataLayerData)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
